import { Form, Formik } from 'formik';
import { useCallback, useEffect } from 'react';

import { Button } from '@hivebrite/design-system/Button';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Card } from '@hivebrite/design-system/Card';

import { useGetInitialValuesStep1 } from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/form/initialValues';
import TicketTable from 'hb-react/shared/apps/Events/components/TicketTable';
import { RegistrationSteps } from 'hb-react/shared/apps/Events/constants/registrationSteps';
import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useRegistrationFlowContext } from 'hb-react/shared/apps/Events/contexts/RegistrationFlowContext';
import { getPromoCodes } from 'hb-react/shared/apps/Events/utils/cartUtils/cartItemsUtils';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import Navigation from '../Navigation';

const TicketSelectionStep = () => {
  const { event, eventDetailsPageLink } = useEventContext();
  const { data, createCart, removeItem } = useEventCartContext();
  const initialValues = useGetInitialValuesStep1(event);
  const { updateRegistrationFlow, setValuesStep1, updateTicket } =
    useRegistrationFlowContext();

  useEffect(() => {
    if (data?.cart?.cart_items?.length > 0) {
      const {
        cart: { cart_items: cartItems },
      } = data;
      const promoCodes = getPromoCodes(cartItems);

      if (promoCodes.length > 0) {
        promoCodes.forEach(({ id }) => removeItem(id));
      }
    }
  }, []);

  const onValidate = useCallback((values) => {
    const errors = {};

    const quantity = values.tickets
      .map(({ quantity }) => Number(quantity))
      .reduce((total, quantity) => total + quantity, 0);

    if (quantity === 0) {
      errors.quantity = __('You must select at least one ticket');
    }

    return errors;
  }, []);

  const onSubmit = async (values) => {
    try {
      await createCart(values);
      setValuesStep1(values);
      updateRegistrationFlow({
        currentStep: RegistrationSteps.TICKET_INFORMATION_STEP,
      });
    } catch (error) {
      errorReporting.captureException(error);
      displayNotif('error', __('Sorry, an error occurred'));
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validate={onValidate}
      onSubmit={onSubmit}
    >
      {({ isValid, isSubmitting }) => (
        <Form>
          <Card bg="white" pt={4} pb={4}>
            <TicketTable
              tickets={event.tickets}
              onUpdate={updateTicket}
              maxTicketsPerUser={event.max_tickets_per_user}
              isLoading={isSubmitting}
            />
          </Card>
          <Navigation>
            {({ isFixed }) => (
              <>
                <ButtonInLink
                  linkProps={{
                    to: eventDetailsPageLink,
                  }}
                  dataTestId="button-cancel-registration"
                  variant="secondary"
                  mr={2}
                >
                  {s__(`NetworkEvents|Cancel registration`)}
                </ButtonInLink>
                <Button
                  dataTestId={`button-${
                    isFixed ? 'next-step' : 'proceed'
                  }-registration`}
                  type="submit"
                  isDisabled={!isValid}
                  isLoading={isSubmitting}
                >
                  {isFixed
                    ? s__('NetworkEvents|Next')
                    : s__('NetworkEvents|Proceed to Registration')}
                </Button>
              </>
            )}
          </Navigation>
        </Form>
      )}
    </Formik>
  );
};

export default TicketSelectionStep;
